<template>
    <section id="login_form" data-cy="login_form">
        <form class="login-form" @submit="login">
            <div class="infos-wrapper">
                <h2 class="welcome">
                    {{ $t("login.welcome") }}
                </h2>
                <div v-if="informations === ''" class="description">
                    {{ $t("login.description") }}
                </div>
                <p v-else class="description" v-html="informations"></p>
            </div>
            <div class="select-wrapper">
                <div
                    :class="{ select: showGestionPart }"
                    @click="selectGestion"
                >
                    {{ $t("MCI.select.gestion") }}
                </div>
                <div
                    :class="{ select: showResidentPart }"
                    @click="selectResident"
                >
                    {{ $t("MCI.select.resident") }}
                </div>
            </div>
            <div class="gestion-container" v-if="showGestionPart">
                <basic-input
                    class="username-field"
                    :disabled="disableLoginButton"
                    v-model="username"
                    name="username"
                    :rules="isNotEmpty"
                    :placeholder="$t('login.id')"
                    :floating="true"
                />
                <password-input
                    class="password-field"
                    :floating="true"
                    :disabled="disableLoginButton"
                    v-model="password"
                    name="password"
                    :rules="isNotEmpty"
                    :placeholder="$t('login.password')"
                />
                <p class="forgot-password" @click="goTo('ForgottenPassword')">
                    {{ $t("login.forgottenPassword") }}
                </p>
                <div class="login-button-wrapper">
                    <basic-button
                        class="login-button"
                        type="submit"
                        :disabled="disableLoginButton"
                        :loading="disableLoginButton"
                    >
                        <span>
                            {{ $t("login.connexion") }}
                        </span>
                    </basic-button>
                </div>
                <BasicDivider class="divider" v-if="isSSOAvailable()">
                    {{ $t("link_words.or").toLowerCase() }}
                </BasicDivider>
                <div class="other-connexions">
                    <BasicButton
                        color="blue"
                        class="button"
                        :loading="ssoMicrosoftLoading"
                        :disabled="ssoMicrosoftLoading"
                        @click="loginWithMicrosoft"
                        v-if="isSSOAvailable()"
                    >
                        <i class="fa-brands fa-microsoft" />
                        <p>{{ $t("sso.connection") }}</p>
                    </BasicButton>
                    <p>
                        {{ $t("login.noAccount") }}
                        <router-link to="register" class="register-link">
                            {{ $t("register.title") }}
                        </router-link>
                    </p>
                </div>
            </div>
            <div class="resident-container" v-if="showResidentPart">
                <p>{{ $t("MCI.access") }}</p>
                <div class="mci-button">
                    <Basic-Button @click="goTo('MCI')">
                        {{ $t("MCI.title") }}
                    </Basic-Button>
                </div>
            </div>
        </form>
    </section>
</template>

<script>
import httpIntratone from "@/services/httpIntratone.js"
import BasicInput from "@/components/basic/BasicInput.vue"
import BasicButton from "@/components/basic/BasicButton.vue"
import PasswordInput from "@/components/basic/PasswordInput.vue"
import BasicDivider from "@/components/basic/BasicDivider.vue"
import { getHomeMessage } from "@/services/intratone/home.js"
import { useToast } from "vue-toastification"
import { useForm } from "vee-validate"
import { saveVersionError } from "@/services/intratone/version"
import { getMicrosoftSSOLink } from "@/services/intratone/sso"
import { isSSOAvailable } from "@/helpers/countryRestrictions"
import navigation from "@/mixins/navigation"
import connexion from "@/mixins/connexion"
import DOMPurify from "dompurify"

const toast = useToast()

export default {
    name: "Login",
    components: {
        BasicInput,
        BasicButton,
        BasicDivider,
        PasswordInput,
    },
    mixins: [navigation, connexion],
    inject: ["emitter"],
    setup() {
        const { validate, errors } = useForm()

        return {
            validate,
            errors,
        }
    },
    computed: {
        isMFACode() {
            return this.$store.getters["auth/getCode"]
        },
        getEnvironnement() {
            return this.$store.getters["auth/getParam"]
        },
    },
    mounted() {
        // Listen emitter event for modal
        this.emitter.on("close-modal", this.handleEvent)
    },
    data() {
        return {
            username: "",
            password: "",
            disableLoginButton: false,
            informations: "",
            attempt: 0,
            errorMessage: null,
            needToCloseModale: null,
            uuid: null,
            showGestionPart: true,
            showResidentPart: false,
            SID: null,
            ssoMicrosoftLoading: false,
        }
    },
    created() {
        this.autoConnexion()
        this.updateFront()
        this.getMessage()
    },
    methods: {
        isSSOAvailable,
        isInCodeTable(email, table) {
            const isInCodeTable = table.find((item) => item.login === email)

            if (isInCodeTable) {
                return isInCodeTable
            } else {
                return false
            }
        },
        // If modal is closed
        handleEvent(payload) {
            this.$store.dispatch("doubleAuth/setErrorMessage", "")
            this.attempt = 0
            this.disableLoginButton = false
            clearTimeout(this.needToCloseModale)
        },
        goTo(page) {
            let url = ""
            switch (page) {
                case "MCI":
                    if (this.getEnvironnement === "dev") {
                        url = "https://dev.moncompte.intratone.fr/"
                    } else if (this.getEnvironnement === "test") {
                        url = "https://test.moncompte.intratone.fr/"
                    } else {
                        url = "https://moncompte.intratone.fr/"
                    }

                    switch (this.$i18n.locale) {
                        case "fr":
                            url = url + "fr/"
                            break

                        case "en":
                        case "dk":
                        case "fi":
                        case "no":
                        case "se":
                            url = url + "en/"
                            break

                        case "nl":
                            url = url + "nl/"
                            break

                        case "de":
                            url = url + "de/"
                            break
                        case "it":
                            url = url + "it/"
                            break

                        case "es":
                            url = url + "es/"
                            break

                        default:
                            url = url + "fr/"
                            break
                    }

                    window.open(url, "_blank")
                    break

                default:
                    this.$router.push({ name: page })
                    break
            }
        },
        authModale(email, username) {
            this.openModal("double-auth", {
                title: "doubleAuth.title",
                email: email,
                valid: async (code) => {
                    await this.loginWithMFA(code, username)
                },
                refuse: () => {
                    this.closeModal("double-auth")
                },
            })
        },
        generateUUID() {
            return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
                /[xy]/g,
                function (c) {
                    var r = (Math.random() * 16) | 0,
                        v = c == "x" ? r : (r & 0x3) | 0x8
                    return v.toString(16)
                },
            )
        },
        autoConnexion() {
            if (window.location.href.search("protocol=true") > 0) {
                httpIntratone.post("http.php").then((response) => {
                    this.connexion(response)
                })
            } else if (
                location.protocol === "http:" &&
                window.location.hostname !== "localhost" &&
                window.location.hostname !== "192.168.137.1"
            ) {
                location.protocol = "https:"
            }
        },
        updateFront() {
            const versionFront =
                this.$store.getters["account/getVersion"].split(".")
            httpIntratone.post("version.php").then((response) => {
                const versionBack = response.data.v.split(".")
                const reloadString = "reload=true"

                versionBack.forEach((value, index) => {
                    if (value > versionFront[index]) {
                        if (window.location.href.search("manuel=true") > 0) {
                            saveVersionError({
                                vClient:
                                    this.$store.getters["account/getVersion"],
                                vCurrent: response.data.v,
                            })
                        } else if (
                            window.location.href.search(reloadString) > 0
                        ) {
                            toast.warning(
                                this.$t("alerts.version.new_version"),
                                {
                                    icon: "fas fa-sync",
                                },
                            )
                        } else {
                            window.history.pushState(
                                {},
                                "Intratone - Site de gestion",
                                `${window.location.href}?${reloadString}`,
                            )
                            this.totalClear()
                        }
                    }
                })
            })
        },
        async getMessage() {
            try {
                // Requesting API to get the global message
                const params = {
                    lng: this.$i18n.locale,
                }
                let messageResponse = await getHomeMessage(params)
                if (messageResponse === false) {
                    throw new Error(this.$t("alerts.errors"))
                }

                if (
                    messageResponse.data.text &&
                    messageResponse.data.text !== ""
                ) {
                    this.informations = DOMPurify.sanitize(
                        messageResponse.data.text,
                    )
                }
            } catch (error) {
                console.log(error)
                toast.error(error.message, {
                    icon: "fas fa-exclamation-circle",
                    timeout: 10000,
                })
            }
        },
        async loginWithMFA(code, username) {
            let params_login = new URLSearchParams()

            params_login.append("user", username)
            params_login.append("code", code)
            params_login.append("device", this.uuid)
            params_login.append("mfa_login", 1)

            await httpIntratone
                .post("connexion.php", params_login)
                .then((response) => {
                    if (response.data.terms_of_uses && !response.data.jwt) {
                        this.closeModal()
                        this.SID = response.data.SID
                        this.termsOfUsesModale(response.data.terms_of_uses.url)
                    } else if (response.data.code === "MFA_CODE_INVALID") {
                        this.attempt++
                        if (this.attempt < 3) {
                            this.$store.dispatch(
                                "doubleAuth/setErrorMessage",
                                this.$t(
                                    "global.modal.doubleAuth.error.attempt",
                                    { count: 3 - this.attempt },
                                ),
                            )
                        } else if (this.attempt >= 3) {
                            this.$store.dispatch(
                                "doubleAuth/setErrorMessage",
                                this.$t("global.modal.doubleAuth.error.retry"),
                            )

                            this.attempt = 0
                            this.disableLoginButton = false

                            this.needToCloseModale = setTimeout(() => {
                                this.closeModal("double-auth")
                                this.$store.dispatch(
                                    "doubleAuth/setErrorMessage",
                                    "",
                                )
                            }, 10000)
                        }
                    } else if (response.data.code === "MFA_CODE_EXPIRED") {
                        this.$store.dispatch(
                            "doubleAuth/setErrorMessage",
                            this.$t("global.modal.doubleAuth.error.expired"),
                        )
                    } else {
                        const newUserSaved = {
                            login: username,
                            uuid: this.uuid,
                            code: code,
                        }

                        const isExist = this.isInCodeTable(
                            username,
                            this.isMFACode,
                        )

                        if (!isExist) {
                            this.$store.commit("auth/setCode", newUserSaved)
                        } else {
                            const array = this.$store.getters["auth/getCode"]

                            const index = array.filter((item) => {
                                return item.login !== username
                            })

                            index.push(newUserSaved)

                            this.$store.commit("auth/setUpdateCode", index)
                        }

                        this.$store.commit(
                            "auth/setSignature",
                            response.data.mfa_signature,
                        )

                        this.connexion(response)
                        this.$store.dispatch("doubleAuth/setErrorMessage", "")
                    }
                })
        },

        loginWithMicrosoft() {
            this.ssoMicrosoftLoading = true

            let params_login = new URLSearchParams()

            params_login.append("lng", window.location.pathname.split("/")[1])
            httpIntratone.post("../sso/init.php", params_login)

            getMicrosoftSSOLink()
                .then((response) => {
                    window.location.href = response.data.microsoft_url
                })
                .finally(() => {
                    this.ssoMicrosoftLoading = false
                })
        },

        async login(e) {
            e.preventDefault()

            try {
                // Disabling login button to prevent button smash
                this.disableLoginButton = true

                const username = this.username
                const isInCodeTable = this.isInCodeTable(
                    username,
                    this.isMFACode,
                )

                let params_login = new URLSearchParams()

                // Validating form
                let validationResponse = await this.validate()
                if (!validationResponse.valid) {
                    throw new Error(this.$t("alerts.connection.invalid"))
                }

                // Check if user is already in code table

                if (!isInCodeTable) {
                    this.uuid = this.generateUUID()
                } else {
                    this.uuid = isInCodeTable.uuid
                    params_login.append("code", isInCodeTable.code)
                }

                params_login.append("identifiant", this.username)
                params_login.append("mdp", this.password)
                params_login.append("device", this.uuid)

                // Requesting v1 site for user authentification
                await httpIntratone
                    .post("connexion.php", params_login)
                    .then((response) => {
                        if (response.data.mfa && !response.data.jwt) {
                            this.authModale(response.data.email, username)
                        } else if (
                            response.data.terms_of_uses &&
                            !response.data.jwt
                        ) {
                            this.SID = response.data.SID
                            this.termsOfUsesModale(
                                response.data.terms_of_uses.url,
                            )
                        } else {
                            this.connexion(response)
                        }
                    })
            } catch (error) {
                this.disableLoginButton = false
                console.log(error)
                toast.error(error.message, {
                    icon: "fas fa-exclamation-circle",
                    timeout: 10000,
                })
            }
        },

        termsOfUsesModale(url) {
            this.openModal("terms-of-uses", {
                title: "termsOfUses.title",
                descriptionKey: "login.termsOfUses.message",
                SID: this.SID,
                connect: (arg) => {
                    this.connexion(arg)
                },
                url: url,
            })
        },

        isNotEmpty(value) {
            if (value === "" || value === null || value === undefined) {
                return this.$t("input.empty")
            }
            return true
        },

        selectGestion() {
            this.showGestionPart = true
            this.showResidentPart = false
        },

        selectResident() {
            this.showGestionPart = false
            this.showResidentPart = true
        },

        initHomeToBreadCrumb(direction) {
            this.$store.commit("breadCrumb/setClassicBreadCrumb", [
                {
                    name: this.$t("global.entities.home"),
                    link:
                        direction === "résidences"
                            ? "/residences"
                            : "/v1/data/accueil/accueil",
                },
            ])
        },

        detectDevice() {
            const userAgent = navigator.userAgent
            switch (userAgent) {
                case /iPhone|Android.*Mobile|Windows Phone|webOS|BlackBerry/i.test(
                    userAgent,
                ):
                    return "mobile"
                case /iPad|Android(?!.*Mobile)|Tablet|Kindle|Silk|PlayBook/i.test(
                    userAgent,
                ):
                    return "tablet"
                case /Windows NT|Macintosh|Linux|CrOS/i.test(userAgent):
                    return "desktop"
                default:
                    return "desktop"
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/global.scss";
@import "@/assets/scss/variables/fontSize.scss";

@media all and (min-width: 1024px) {
    #login_form {
        display: flex;
        flex-direction: column;
        flex: 1;
        width: 100%;
        height: 100%;

        .login-form {
            flex: 1;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
        }
    }
}

@media all and (min-width: 768px) and (max-width: 1024px) {
    #login_form {
        height: 100%;
    }
    .login-form {
        width: 60vw;
        margin: unset;
        overflow: auto;
        display: flex;
        height: 100%;
        flex-direction: column;
        justify-content: center;
    }
}

@media all and (max-width: 768px) {
    #login_form {
        height: 100%;
    }
    .login-form {
        width: 100%;
        margin: unset;
        overflow: auto;
        display: flex;
        height: 100%;
        flex-direction: column;
        justify-content: center;
    }
}

.login-form {
    text-align: center;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -ms-flex-direction: column;
    flex-direction: column;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    -webkit-box-pack: center;
    -ms-flex-pack: center;
    justify-content: center;
    font-size: $normal;
    font-weight: 300;
    line-height: 19px;

    .infos-wrapper {
        display: flex;
        flex-direction: column;
        gap: 20px;
        text-align: start;
        padding: 2em 0;

        .description {
            max-height: 100px;
            height: max-content;
            overflow-y: auto;
            white-space: pre-line;
        }
    }
}

.welcome {
    font-family: $font_avenir_black;
    font-size: $veryBig;
    margin: 0;
}

.description {
    font-family: $font_avenir_book;
    font-size: $normal;
    margin: 0;
}

.select-wrapper {
    width: 100%;
    display: flex;
    background: rgba($pink-lighter, $alpha: 0.4);
    border-bottom: 3px solid $pink-lighter;
    border-radius: 10px 10px 0 0;
    font-family: $font_avenir_heavy;
    font-size: 16px;

    div {
        padding: 1em 2em;
        transition: all 0.2s ease-in-out;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;

        @media all and (max-width: 1024px) {
            padding: 1em 0.5em;
        }

        &:hover {
            cursor: pointer;
            background-color: $pink-lighter;
            border-radius: 10px 10px 0 0;
            transition: all 0.2s ease-in-out;
        }
    }

    .select {
        background-color: $pink-lighter;
        border-radius: 10px 10px 0 0;
        transition: all 0.2s ease-in-out;

        &:hover {
            cursor: default;
        }
    }
}

.gestion-container {
    width: 100%;
    padding-top: 20px;

    .divider {
        text-wrap: nowrap;
        padding-bottom: 20px;
    }

    .other-connexions {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;

        .button {
            width: max-content;
            display: flex;
            align-items: center;
            gap: 0.5em;

            p {
                color: $white;
            }
        }

        p {
            color: $grey;
            margin: 0;
        }
    }
}

.resident-container {
    width: 100%;
    padding-top: 20px;
    height: 335px;

    p {
        margin: 0;
    }

    .mci-button {
        padding-top: 20px;
    }
}

.username-field {
    width: 100%;
    margin-bottom: 15px;

    input {
        text-align: center;
        height: 60px;
    }

    .form-floating > label {
        width: 100%;
    }
}

.password-field {
    width: 100%;
    margin-bottom: 15px;

    input {
        text-align: center;
        height: 60px;
    }

    .form-floating > label {
        width: 100%;
    }
}

.forgot-password {
    text-align: start;
    font-size: $small;
    color: $grey;
    text-decoration: underline;
}

.forgot-password:hover {
    color: $blue-light;
    cursor: pointer;
}

.login-button-wrapper {
    display: flex;
    width: 100%;
    justify-content: center;
}

.login-button {
    margin-bottom: 20px;
}

.register-link {
    font-family: $font_avenir_heavy;
    color: $orange-neutral;
    text-underline-offset: 4px;
    text-decoration: underline;
}

.register-link:hover {
    color: $blue-lighter;
}
</style>
